import CT from '@marketplace-ai/tags';
import { ActorRefFrom } from 'xstate';
import { STATES } from '~/constants';
import { OnSendDeleteConfirmation } from '~/features/events';
import {
  convertWarningsKeys,
  sendDeleteConfirmation,
  transformCodeToSelectOption,
} from '~/features/utils';
import { CDSLicense, DEALicense } from '~/types';
import { CdsLicenseFormValues } from '../cds/types';
import { DeaLicenseFormValues } from '../dea/types';
import type { licensesFormMachine } from '../licenseForm.machine';
import {
  CdsSortOptions,
  Context,
  DeaSortOptions,
  Events,
  GetResponse,
  OnEditEvent,
  OnFilterEvent,
  OnSortTableEvent,
  OutputType,
  Scope,
  Table,
} from './types';

export { LICENSES_TABS } from './constants';
export { LicensesRefContext, useLicensesActorRef } from './provider';

export const updateFilter = ({
  table,
  event,
}: {
  table: Table;
  event: { filter: string };
}): Table => {
  const { filter } = event;
  if (filter === 'all') {
    return { ...table, filter: { active: true, inactive: true } };
  } else if (filter === 'active') {
    return { ...table, filter: { active: true, inactive: false } };
  } else if (filter === 'inactive') {
    return { ...table, filter: { active: false, inactive: true } };
  } else {
    return { ...table };
  }
};

export const filterOptionAsString = (value: { active: boolean; inactive: boolean }) => {
  if (value.active && value.inactive) {
    return 'all';
  } else if (value.active) {
    return 'active';
  } else if (value.inactive) {
    return 'inactive';
  } else {
    return 'all';
  }
};

export const filterOptionAsObject = (value: string) => {
  if (value === 'all') {
    return { active: true, inactive: true };
  } else if (value === 'active') {
    return { active: true, inactive: false };
  } else if (value === 'inactive') {
    return { active: false, inactive: true };
  } else {
    console.error('unknown filter activated');
    return { active: true, inactive: true };
  }
};

export const getFilter = (actor: ActorRefFrom<typeof licensesFormMachine>, mode: 'cds' | 'dea') => {
  return {
    filterOptions: [
      { value: { active: true, inactive: true }, label: 'All Licenses' },
      { value: { active: true, inactive: false }, label: 'Active Licenses' },
      { value: { active: false, inactive: true }, label: 'Inactive Licenses' },
    ],
    cyTag: CT.account.licenses[mode].selectActiveFilter,
    onFilterChange: (e: React.ChangeEvent<HTMLSelectElement>) =>
      actor.send({ type: 'ON_FILTER_TABLE', payload: filterOptionAsObject(e.target.value) }),
    filterOptionAsString: (value: { active: boolean; inactive: boolean }) =>
      filterOptionAsString(value),
  };
};

export const populateDeaForm = ({ context, event }: { context: Context; event: Events }) => {
  const { id } = event as OnEditEvent;
  const [license] = context.deaLicenses.filter((l) => l.id === id);
  // AI-parsed records may return null for fields the form requires.
  return {
    id: license.id,
    activeFlag: license.activeFlag ?? false,
    deaNum: license.deaNum ?? '',
    stateCode: license.stateCode ?? '',
    schedule: license.schedule ?? '',
    expiryDate: license.expiryDate ?? '',
    issueDate: license.issueDate ?? '',
    licenseNotes: license.licenseNotes ?? '',
    providerId: license.providerId,
    restrictedUseFlag: license.restrictedUseFlag ?? false,
    validFlag: license.validFlag ?? true,
    warnings: convertWarningsKeys(license.warnings),
  };
};

export const populateCdsForm = ({ context, event }: { context: Context; event: Events }) => {
  const { id } = event as OnEditEvent;
  const [license] = context.cdsLicenses.filter((l) => l.id === id);

  // Context stores transformed form values, so issuingStateCode is already an option object.
  const stateCodeValue =
    typeof license.issuingStateCode === 'object' && license.issuingStateCode !== null
      ? (license.issuingStateCode as { value: string }).value
      : ((license.issuingStateCode as string) ?? '');

  const issuingStateCode = transformCodeToSelectOption(stateCodeValue, STATES, {
    label: 'Unknown',
    value: stateCodeValue || '',
  });

  // AI-parsed records may return null for fields the form requires.
  return {
    id: license.id,
    activeFlag: license.activeFlag ?? false,
    expiryDate: license.expiryDate ?? '',
    issueDate: license.issueDate ?? '',
    issuingStateCode: issuingStateCode,
    issuingAgency: license.issuingAgency ?? '',
    licenseNum: license.licenseNum ?? '',
    licenseTitle: license.licenseTitle ?? '',
    providerId: license.providerId,
    validFlag: license.validFlag ?? true,
    warnings: convertWarningsKeys(license.warnings),
  };
};

export const emptyDeaForm: DeaLicenseFormValues = {
  id: -1,
  activeFlag: false,
  deaNum: '',
  stateCode: '',
  schedule: '',
  expiryDate: '',
  issueDate: '',
  licenseNotes: '',
  restrictedUseFlag: false,
};

export const emptyCdsForm: CdsLicenseFormValues = {
  id: -1,
  activeFlag: false,
  expiryDate: '',
  issueDate: '',
  issuingStateCode: STATES[0],
  issuingAgency: '',
  licenseNum: '',
  licenseTitle: '',
};

export const cdsApiToForms = (licensesStr: string): GetResponse<CdsLicenseFormValues> => {
  const licenses: GetResponse<CDSLicense> = JSON.parse(licensesStr);
  return {
    ...licenses,
    data: licenses.data.map((license) =>
      cdsApiToForm(JSON.stringify(license))
    ) as CdsLicenseFormValues[],
  };
};

export const cdsApiToForm = (licenseStr: string): CdsLicenseFormValues => {
  const license: CDSLicense = JSON.parse(licenseStr);

  // AI-parsed records may return null for fields the form requires.
  return {
    id: license.id,
    activeFlag: license.activeFlag ?? false,
    licenseNum: license.licenseNum ?? '',
    expiryDate: license.expiryDate ?? '',
    issueDate: license.issueDate ?? '',
    licenseTitle: license.licenseTitle ?? '',
    issuingAgency: license.issuingAgency ?? '',
    issuingStateCode: transformCodeToSelectOption(license.issuingStateCode ?? '', STATES, {
      label: 'Unknown',
      value: license.issuingStateCode ?? '',
    }),
    providerId: license.providerId,
    validFlag: license.validFlag ?? true,
    warnings: convertWarningsKeys(license.warnings),
  };
};

export const setDeaTableFilter = ({ context, event }: Scope) => {
  const { payload } = event as OnFilterEvent;
  return {
    ...context.deaTable,
    filter: payload,
  };
};

export const setCdsTableFilter = ({ context, event }: Scope) => {
  const { payload } = event as OnFilterEvent;
  return {
    ...context.cdsTable,
    filter: payload,
  };
};

export const setCdsLicensesFromResponse = ({ event }: Scope) => {
  const data = (event as OutputType).output as unknown as GetResponse<CdsLicenseFormValues>;
  return data.data;
};

export const setCdsTableFromResponse = ({ context, event }: Scope) => {
  const { page, pageSize, total } = (event as OutputType).output as GetResponse<CDSLicense>;
  return {
    ...context.cdsTable,
    pagination: { page, pageSize, total },
  };
};

export const setDeaLicensesFromResponse = ({ event }: Scope): DEALicense[] => {
  const { data } = (event as OutputType).output as GetResponse<DEALicense>;
  return data as DEALicense[];
};

export const setDeaTableFromResponse = ({ context, event }: Scope) => {
  const data = (event as OutputType).output as GetResponse<DEALicense>;
  const { page, pageSize, total } = data;
  return {
    ...context.deaTable,
    pagination: { page, pageSize, total },
  };
};

export const setCdsFromOutput = {
  cdsLicenses: setCdsLicensesFromResponse,
  cdsTable: setCdsTableFromResponse,
};

export const setDeaFromOutput = {
  deaLicenses: setDeaLicensesFromResponse,
  deaTable: setDeaTableFromResponse,
};

export const setDeaTableSort = ({ context, event }: Scope) => {
  const { key } = event as OnSortTableEvent;
  const _key = key as DeaSortOptions;
  return {
    ...context.deaTable,
    sort: {
      ...context.deaTable.sort,
      active: _key,
      [_key]: -1 * context.deaTable.sort[_key],
    },
  };
};

export const setCdsTableSort = ({ context, event }: Scope) => {
  const { key } = event as OnSortTableEvent;
  const _key = key as CdsSortOptions;
  return {
    ...context.cdsTable,
    sort: {
      ...context.cdsTable.sort,
      active: _key,
      [_key]: -1 * context.cdsTable.sort[_key],
    },
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const openDeleteConfirmation = (args: any) => {
  const { event, system, self } = args;
  const deleteEvent = event as OnSendDeleteConfirmation;

  const onConfirm = (id: number) => {
    // Use self to send event back to this machine
    if (self) {
      self.send({ type: 'ON_DELETE', payload: { id } });
    } else {
      console.warn('[licenses] Unable to send ON_DELETE - self is undefined');
    }
  };
  // Send confirmation to userState (which handles the confirmation dialog)
  const userActor = system.get('userState');
  if (userActor) {
    userActor.send(sendDeleteConfirmation(() => onConfirm(deleteEvent.payload.id)));
  } else {
    console.warn('[licenses] Unable to send confirmation - userState not found in system');
  }
};
